import { type FC } from 'react';
import Quill from 'quill';

import {
	RolloverText,
	RefWithAttributes,
	BlockWithAttributes,
	DivWithAttributes,
	CustomLink,
	CustomBreak,
	Cite
} from './CustomFormats';

import { CustomH1, CustomH2, CustomH3, CustomH4, CustomH5, EyebrowHeading } from './CustomHeadings';
import {
	FootnoteRefTail,
	WebtextFigureReference,
	WebtextSourceUrl,
	WebtextMath,
	VisuallyHidden,
	WebtextGlossaryTermLocations,
	WebtextBigLinkTitle,
	WebtextBigLinkCredit,
	WebtextPullquoteHeading,
	KeyPoint,
	WebtextArticleSourceName,
	IconTechTip,
	WebtextDecoration
} from './CustomSpans';

Quill.register(RolloverText, true);
Quill.register(RefWithAttributes, true);
Quill.register(BlockWithAttributes, true);
Quill.register(DivWithAttributes, true);
Quill.register(CustomLink, true);
Quill.register(CustomBreak, true);
Quill.register(Cite, true);
Quill.register(EyebrowHeading, true);

Quill.register(FootnoteRefTail, true);
Quill.register(WebtextFigureReference, true);
Quill.register(WebtextSourceUrl, true);
Quill.register(WebtextMath, true);
Quill.register(VisuallyHidden, true);
Quill.register(WebtextGlossaryTermLocations, true);
Quill.register(WebtextBigLinkTitle, true);
Quill.register(WebtextBigLinkCredit, true);
Quill.register(WebtextPullquoteHeading, true);
Quill.register(KeyPoint, true);
Quill.register(WebtextArticleSourceName, true);
Quill.register(IconTechTip, true);
Quill.register(WebtextDecoration, true);

Quill.register(CustomH1, true);
Quill.register(CustomH2, true);
Quill.register(CustomH3, true);
Quill.register(CustomH4, true);
Quill.register(CustomH5, true);

export type ToolbarOption =
	| 'h1'
	| 'h2'
	| 'h3'
	| 'h4'
	| 'h5'
	| 'eyebrowHeading'
	| 'bold'
	| 'italic'
	| 'underline'
	| 'list-ordered'
	| 'list-bullet'
	| 'script-sub'
	| 'script-super'
	| 'link'
	| 'blockquote'
	| 'rolloverText';

interface Props {
	toolbar_id: string;
	allowedOptions?: ToolbarOption[];
}

const h1 = (
	<svg viewBox="0 0 18 18">
		<path
			className="ql-fill"
			d="M10,4V14a1,1,0,0,1-2,0V10H3v4a1,1,0,0,1-2,0V4A1,1,0,0,1,3,4V8H8V4a1,1,0,0,1,2,0Zm6.06787,9.209H14.98975V7.59863a.54085.54085,0,0,0-.605-.60547h-.62744a1.01119,1.01119,0,0,0-.748.29688L11.645,8.56641a.5435.5435,0,0,0-.022.8584l.28613.30762a.53861.53861,0,0,0,.84717.0332l.09912-.08789a1.2137,1.2137,0,0,0,.2417-.35254h.02246s-.01123.30859-.01123.60547V13.209H12.041a.54085.54085,0,0,0-.605.60547v.43945a.54085.54085,0,0,0,.605.60547h4.02686a.54085.54085,0,0,0,.605-.60547v-.43945A.54085.54085,0,0,0,16.06787,13.209Z"
		/>
	</svg>
);

const h2 = (
	<svg viewBox="0 0 18 18">
		<path
			className="ql-fill"
			d="M16.73975,13.81445v.43945a.54085.54085,0,0,1-.605.60547H11.855a.58392.58392,0,0,1-.64893-.60547V14.0127c0-2.90527,3.39941-3.42187,3.39941-4.55469a.77675.77675,0,0,0-.84717-.78125,1.17684,1.17684,0,0,0-.83594.38477c-.2749.26367-.561.374-.85791.13184l-.4292-.34082c-.30811-.24219-.38525-.51758-.1543-.81445a2.97155,2.97155,0,0,1,2.45361-1.17676,2.45393,2.45393,0,0,1,2.68408,2.40918c0,2.45312-3.1792,2.92676-3.27832,3.93848h2.79443A.54085.54085,0,0,1,16.73975,13.81445ZM9,3A.99974.99974,0,0,0,8,4V8H3V4A1,1,0,0,0,1,4V14a1,1,0,0,0,2,0V10H8v4a1,1,0,0,0,2,0V4A.99974.99974,0,0,0,9,3Z"
		/>
	</svg>
);

const h3 = (
	<svg viewBox="0 0 18 18">
		<path
			className="ql-fill"
			d="M16.65186,12.30664a2.6742,2.6742,0,0,1-2.915,2.68457,3.96592,3.96592,0,0,1-2.25537-.6709.56007.56007,0,0,1-.13232-.83594L11.64648,13c.209-.34082.48389-.36328.82471-.1543a2.32654,2.32654,0,0,0,1.12256.33008c.71484,0,1.12207-.35156,1.12207-.78125,0-.61523-.61621-.86816-1.46338-.86816H13.2085a.65159.65159,0,0,1-.68213-.41895l-.05518-.10937a.67114.67114,0,0,1,.14307-.78125l.71533-.86914a8.55289,8.55289,0,0,1,.68213-.7373V8.58887a3.93913,3.93913,0,0,1-.748.05469H11.9873a.54085.54085,0,0,1-.605-.60547V7.59863a.54085.54085,0,0,1,.605-.60547h3.75146a.53773.53773,0,0,1,.60547.59375v.17676a1.03723,1.03723,0,0,1-.27539.748L14.74854,10.0293A2.31132,2.31132,0,0,1,16.65186,12.30664ZM9,3A.99974.99974,0,0,0,8,4V8H3V4A1,1,0,0,0,1,4V14a1,1,0,0,0,2,0V10H8v4a1,1,0,0,0,2,0V4A.99974.99974,0,0,0,9,3Z"
		/>
	</svg>
);

const h4 = (
	<svg viewBox="0 0 18 18">
		<path
			className="ql-fill"
			d="M10,4V14a1,1,0,0,1-2,0V10H3v4a1,1,0,0,1-2,0V4A1,1,0,0,1,3,4V8H8V4a1,1,0,0,1,2,0Zm7.05371,7.96582v.38477c0,.39648-.165.60547-.46191.60547h-.47314v1.29785a.54085.54085,0,0,1-.605.60547h-.69336a.54085.54085,0,0,1-.605-.60547V12.95605H11.333a.5412.5412,0,0,1-.60547-.60547v-.15332a1.199,1.199,0,0,1,.22021-.748l2.56348-4.05957a.7819.7819,0,0,1,.72607-.39648h1.27637a.54085.54085,0,0,1,.605.60547v3.7627h.33008A.54055.54055,0,0,1,17.05371,11.96582ZM14.28125,8.7207h-.022a4.18969,4.18969,0,0,1-.38525.81348l-1.188,1.80469v.02246h1.5293V9.60059A7.04058,7.04058,0,0,1,14.28125,8.7207Z"
		/>
	</svg>
);

const h5 = (
	<svg viewBox="0 0 18 18">
		<path
			className="ql-fill"
			d="M16.74023,12.18555a2.75131,2.75131,0,0,1-2.91553,2.80566,3.908,3.908,0,0,1-2.25537-.68164.54809.54809,0,0,1-.13184-.8252L11.73438,13c.209-.34082.48389-.36328.8252-.1543a2.23757,2.23757,0,0,0,1.1001.33008,1.01827,1.01827,0,0,0,1.1001-.96777c0-.61621-.53906-.97949-1.25439-.97949a2.15554,2.15554,0,0,0-.64893.09961,1.15209,1.15209,0,0,1-.814.01074l-.12109-.04395a.64116.64116,0,0,1-.45117-.71484l.231-3.00391a.56666.56666,0,0,1,.62744-.583H15.541a.54085.54085,0,0,1,.605.60547v.43945a.54085.54085,0,0,1-.605.60547H13.41748l-.04395.72559a1.29306,1.29306,0,0,1-.04395.30859h.022a2.39776,2.39776,0,0,1,.57227-.07715A2.53266,2.53266,0,0,1,16.74023,12.18555ZM9,3A.99974.99974,0,0,0,8,4V8H3V4A1,1,0,0,0,1,4V14a1,1,0,0,0,2,0V10H8v4a1,1,0,0,0,2,0V4A.99974.99974,0,0,0,9,3Z"
		/>
	</svg>
);

const eyebrowHeading = (
	<svg viewBox="0 0 15 15" xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M3.6255 2.75C3.83478 2.75 4.02192 2.88034 4.09448 3.07664L7.16985 11.3962C7.2656 11.6552 7.13324 11.9428 6.87423 12.0386C6.61522 12.1343 6.32763 12.002 6.23188 11.7429L5.22387 9.01603H2.02712L1.01911 11.7429C0.923362 12.002 0.635774 12.1343 0.376762 12.0386C0.117749 11.9428 -0.0146052 11.6552 0.0811401 11.3962L3.15651 3.07664C3.22908 2.88034 3.41621 2.75 3.6255 2.75ZM3.6255 4.69207L4.90966 8.16603H2.34133L3.6255 4.69207ZM11.3719 2.75C11.5811 2.75 11.7683 2.88034 11.8408 3.07664L14.9162 11.3962C15.012 11.6552 14.8796 11.9428 14.6206 12.0386C14.3616 12.1343 14.074 12.002 13.9782 11.7429L12.9702 9.01603H9.77348L8.76547 11.7429C8.66972 12.002 8.38213 12.1343 8.12312 12.0386C7.86411 11.9428 7.73175 11.6552 7.8275 11.3962L10.9029 3.07664C10.9754 2.88034 11.1626 2.75 11.3719 2.75ZM11.3719 4.69207L12.656 8.16603H10.0877L11.3719 4.69207Z"
			fill="currentColor"></path>
	</svg>
);
const BUTTON_GROUPS: { options: ToolbarOption[]; render: (key: ToolbarOption) => JSX.Element }[] = [
	{
		options: ['h1', 'h2', 'h3', 'h4', 'h5'],
		render: (key) => {
			const icons = { h1, h2, h3, h4, h5 };
			return (
				<button key={key} className={`ql-${key}`}>
					{icons[key]}
				</button>
			);
		}
	},
	{
		options: ['eyebrowHeading'],
		render: (key) => (
			<button key={key} className="ql-eyebrowHeading">
				{eyebrowHeading}
			</button>
		)
	},
	{
		options: ['bold', 'italic', 'underline'],
		render: (key) => <button key={key} className={`ql-${key}`} />
	},
	{
		options: ['list-ordered', 'list-bullet'],
		render: (key) => (
			<button key={key} className="ql-list" value={key === 'list-ordered' ? 'ordered' : 'bullet'} />
		)
	},
	{
		options: ['script-sub', 'script-super'],
		render: (key) => (
			<button key={key} className="ql-script" value={key === 'script-sub' ? 'sub' : 'super'} />
		)
	},
	{
		options: ['link', 'blockquote'],
		render: (key) => <button key={key} className={`ql-${key}`} />
	},
	{
		options: ['rolloverText'],
		render: (key) => (
			<button key={key} className="ql-rolloverText">
				Add definition
			</button>
		)
	}
];

const ALL_OPTIONS: ToolbarOption[] = BUTTON_GROUPS.flatMap((g) => g.options);

const QuillToolbar: FC<Props> = (props) => {
	const { toolbar_id, allowedOptions } = props;

	const options = allowedOptions ?? ALL_OPTIONS;
	const show = (key: ToolbarOption) => options.includes(key);

	return (
		<div id={'toolbar' + toolbar_id}>
			{BUTTON_GROUPS.map((group, i) => {
				const buttons = group.options.filter(show).map(group.render);
				return buttons.length > 0 ? (
					<span className="ql-formats" key={i}>
						{buttons}
					</span>
				) : null;
			})}
		</div>
	);
};

export default QuillToolbar;
